import { create } from 'zustand'
import {
  ReportHistoryItem,
  ScheduledReport,
  CreateScheduledReportRequest,
  UpdateScheduledReportRequest,
  ReportType,
  RecipientReference
} from '../types/report'
import { reportService } from '../services/reportService'

interface ReportState {
  // Report Types
  reportTypes: ReportType[]
  reportTypesLoading: boolean
  reportTypesError: string | null

  // Report History
  reportHistory: ReportHistoryItem[]
  historyLoading: boolean
  historyError: string | null
  historyPagination: {
    currentPage: number
    totalPages: number
    totalReports: number
  }

  // Scheduled Reports
  scheduledReports: ScheduledReport[]
  scheduledLoading: boolean
  scheduledError: string | null
  scheduledPagination: {
    currentPage: number
    totalPages: number
    totalScheduledReports: number
  }

  // Actions - Report Types
  loadReportTypes: () => Promise<void>

  // Actions - Report History
  loadReportHistory: (customerId: string, page?: number) => Promise<void>
  deleteReportFromHistory: (reportId: string) => Promise<void>

  // Actions - Scheduled Reports
  loadScheduledReports: (customerId: string, page?: number) => Promise<void>
  createScheduledReport: (data: CreateScheduledReportRequest) => Promise<void>
  updateScheduledReport: (
    scheduleId: string,
    data: UpdateScheduledReportRequest
  ) => Promise<void>
  deleteScheduledReport: (scheduleId: string) => Promise<void>
  toggleScheduleStatus: (scheduleId: string) => Promise<void>
  runScheduleNow: (scheduleId: string) => Promise<void>
  updateScheduleRecipients: (
    scheduleId: string,
    recipientRefs: RecipientReference[]
  ) => Promise<void>

  // Reset
  reset: () => void
}

const useReportStore = create<ReportState>((set, get) => ({
  // Initial State
  reportTypes: [],
  reportTypesLoading: false,
  reportTypesError: null,

  reportHistory: [],
  historyLoading: false,
  historyError: null,
  historyPagination: { currentPage: 1, totalPages: 1, totalReports: 0 },

  scheduledReports: [],
  scheduledLoading: false,
  scheduledError: null,
  scheduledPagination: {
    currentPage: 1,
    totalPages: 1,
    totalScheduledReports: 0
  },

  // Actions - Report Types
  loadReportTypes: async () => {
    set({ reportTypesLoading: true, reportTypesError: null })
    try {
      const response = await reportService.getReportTypes()
      set({
        reportTypes: response.data.reportTypes,
        reportTypesLoading: false
      })
    } catch (error) {
      set({
        reportTypesError:
          error instanceof Error ? error.message : 'Failed to load report types',
        reportTypesLoading: false
      })
    }
  },

  // Actions - Report History (kept for future use, currently no backend endpoint)
  loadReportHistory: async (_customerId, _page = 1) => {
    set({ historyLoading: true, historyError: null })
    try {
      // Report history endpoint not implemented yet
      // When backend is ready, uncomment:
      // const response = await reportService.getReportHistory(customerId, page)
      set({
        reportHistory: [],
        historyPagination: {
          currentPage: 1,
          totalPages: 1,
          totalReports: 0
        },
        historyLoading: false
      })
    } catch (error) {
      set({
        historyError:
          error instanceof Error ? error.message : 'Failed to load report history',
        historyLoading: false
      })
    }
  },

  deleteReportFromHistory: async (reportId) => {
    // Report history endpoint not implemented yet
    set((state) => ({
      reportHistory: state.reportHistory.filter((r) => r.id !== reportId),
      historyPagination: {
        ...state.historyPagination,
        totalReports: state.historyPagination.totalReports - 1
      }
    }))
  },

  // Actions - Scheduled Reports
  loadScheduledReports: async (customerId, page = 1) => {
    set({ scheduledLoading: true, scheduledError: null })
    try {
      const response = await reportService.getScheduledReports(customerId, page)

      set({
        scheduledReports: response.data.scheduledReports,
        scheduledPagination: {
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalScheduledReports: response.data.pagination.totalScheduledReports
        },
        scheduledLoading: false
      })
    } catch (error) {
      set({
        scheduledError:
          error instanceof Error
            ? error.message
            : 'Failed to load scheduled reports',
        scheduledLoading: false
      })
    }
  },

  createScheduledReport: async (data) => {
    const newReport = await reportService.createScheduledReport(data)

    set((state) => ({
      scheduledReports: [newReport, ...state.scheduledReports],
      scheduledPagination: {
        ...state.scheduledPagination,
        totalScheduledReports: state.scheduledPagination.totalScheduledReports + 1
      }
    }))
  },

  updateScheduledReport: async (scheduleId, data) => {
    const updated = await reportService.updateScheduledReport(scheduleId, data)

    set((state) => ({
      scheduledReports: state.scheduledReports.map((r) =>
        r.id === scheduleId ? updated : r
      )
    }))
  },

  deleteScheduledReport: async (scheduleId) => {
    await reportService.deleteScheduledReport(scheduleId)

    set((state) => ({
      scheduledReports: state.scheduledReports.filter((r) => r.id !== scheduleId),
      scheduledPagination: {
        ...state.scheduledPagination,
        totalScheduledReports: state.scheduledPagination.totalScheduledReports - 1
      }
    }))
  },

  toggleScheduleStatus: async (scheduleId) => {
    const schedule = get().scheduledReports.find((r) => r.id === scheduleId)
    if (!schedule) return

    const newStatus = schedule.status === 'active' ? 'paused' : 'active'
    const updated = await reportService.toggleScheduleStatus(scheduleId, newStatus)

    set((state) => ({
      scheduledReports: state.scheduledReports.map((r) =>
        r.id === scheduleId ? updated : r
      )
    }))
  },

  runScheduleNow: async (scheduleId) => {
    await reportService.runScheduleNow(scheduleId)

    // Update the local state to reflect the run
    set((state) => ({
      scheduledReports: state.scheduledReports.map((r) =>
        r.id === scheduleId
          ? {
              ...r,
              lastRunAt: new Date().toISOString(),
              runCount: r.runCount + 1
            }
          : r
      )
    }))
  },

  updateScheduleRecipients: async (scheduleId, recipientRefs) => {
    const updated = await reportService.updateScheduleRecipients(scheduleId, recipientRefs)

    set((state) => ({
      scheduledReports: state.scheduledReports.map((r) =>
        r.id === scheduleId ? updated : r
      )
    }))
  },

  reset: () =>
    set({
      reportTypes: [],
      reportTypesLoading: false,
      reportTypesError: null,
      reportHistory: [],
      historyLoading: false,
      historyError: null,
      historyPagination: { currentPage: 1, totalPages: 1, totalReports: 0 },
      scheduledReports: [],
      scheduledLoading: false,
      scheduledError: null,
      scheduledPagination: {
        currentPage: 1,
        totalPages: 1,
        totalScheduledReports: 0
      }
    })
}))

export default useReportStore
